/* eslint-disable react-refresh/only-export-components --
 * Canonical React Context pattern — co-locates the
 * :class:`MultiPaneApiProvider` component with its consumer
 * hooks (:func:`useMultiPaneApi`, :func:`useRegisterMultiPaneApi`)
 * + the :type:`MultiPaneApi` interface. Splitting hooks into a
 * separate file is the rule's recommendation but produces an
 * orphaned types-only module that obscures the context's
 * single-source-of-truth nature. Fast-refresh degrades to a
 * full reload when this file changes, which is acceptable for
 * a stable infrastructure module that changes rarely.
 */
/**
 * Multi-pane workspace API context — the seam that lets the
 * topbar's :class:`PreferencesMenu` call into the workspace
 * (which lives inside the route's ``<Outlet />``) without
 * either component knowing about the other directly.
 *
 * The :class:`MultiPaneWorkspace` mounted on the ``/`` route
 * registers its dockview-react API + bound action handlers via
 * :func:`useRegisterMultiPaneApi` on mount + unregisters
 * (``null``) on unmount. The preferences-menu reads via
 * :func:`useMultiPaneApi`; it returns ``null`` when no workspace
 * is mounted (e.g., on ``/403`` or unknown routes) so the
 * Panels section of the menu hides per chrome-spec.md 2026-05-26
 * amendment §"UI preferences menu (production)".
 *
 * Part 2 (architect 2026-06-08): the named-preset machinery was
 * removed — ``applyPreset`` / ``currentPresetName`` are gone.
 * Layouts are captured live on save into a Profile; the workspace
 * falls back to :data:`DEFAULT_LAYOUT` (not a ``preset_name`` lookup)
 * when a saved layout is absent / corrupt / role-inaccessible. The
 * surviving primitives are :func:`captureLayout` / :func:`applyLayout`
 * (non-persisting) + :func:`saveCurrentLayout` / :func:`resetLayout`
 * + :func:`togglePanel`.
 *
 * Cross-references:
 *   - ADR-024 §6 (explicit save; two-tier opaque dockview_state).
 *   - REQ-UI-052 / 054 / 055 / 077.
 *   - chrome-spec.md 2026-05-26 amendment §"UI preferences menu".
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import type { PanelId } from "@/multi-pane/registry-types";
import type { SetPreferencesResult } from "@/preferences/preferences-context";
import type { ProfileDocument } from "@/auth/session-types";

/**
 * The Profile document's ``layout`` sub-document (:type:`LayoutSettings`;
 * ADR-024 §6 two-tier model — Meridian semantic layer + opaque
 * ``dockview_state``). Aliased here so the non-persisting capture / apply
 * primitives the Profile editor's Arrange-layout sub-mode consumes
 * (:func:`MultiPaneApi.captureLayout` / :func:`MultiPaneApi.applyLayout`,
 * REQ-UI-077) carry the same generated shape the workspace persists.
 */
export type LayoutSettings = NonNullable<ProfileDocument["layout"]>;

/**
 * The bound action surface the workspace publishes to the
 * preferences-menu. All methods are side-effecting on the
 * mounted dockview-react instance + the preferences context.
 */
export interface MultiPaneApi {
  /**
   * Capture the current dockview state + persist it via
   * ``PUT /api/me/preferences`` per REQ-UI-052. Resolves with
   * the provider's :class:`SetPreferencesResult` so the menu can
   * surface a success result or inline error. The save operation
   * is explicit — drag / dock / resize / close changes do NOT
   * auto-save per REQ-UI-052.
   */
  saveCurrentLayout: () => Promise<SetPreferencesResult>;

  /**
   * Reset the current session's layout to the built-in
   * :data:`DEFAULT_LAYOUT` per REQ-UI-055. Session-only: leaves the
   * user's saved ``panel_layout`` in preferences untouched. The
   * confirmation dialog is owned by the menu (caller); this
   * method assumes the operator has already confirmed.
   */
  resetLayout: () => void;

  /**
   * The set of :type:`PanelId` values currently open in the
   * dockview workspace. Drives the Add panel menu's
   * open/closed indicator dot + the toggle's add-vs-focus
   * branch. Updates reactively when the workspace's dockview
   * fires ``onDidAddPanel`` / ``onDidRemovePanel`` events
   * (Stream C PR 2 wires the listener; ADR-024 §2 inventory).
   */
  readonly openPanelIds: ReadonlySet<PanelId>;

  /**
   * A monotonic counter bumped on **every** dockview layout change —
   * drag / dock / resize / split / close, not just add/remove (the
   * workspace subscribes to dockview's ``onDidLayoutChange``). It is the
   * reactive *layout-changed* signal the "(modified)" indicator needs:
   * the live panel arrangement (``captureLayout()``) is only snapshotted
   * imperatively at save time, so without this nonce a re-render trigger,
   * a pure-layout rearrange (which never touches the working state) would
   * leave "Profile: «name» (modified)" reading "not modified" while
   * Update-current finds a real difference (REQ-UI-074 — the indicator and
   * the update diff must agree). Consumers re-read ``captureLayout()`` when
   * this value changes; the number itself carries no meaning beyond
   * "something about the layout moved" (ADR-024 §6).
   *
   * Starts at ``0`` and increments after ``onReady`` wires the listener, so
   * the initial ``fromJSON`` mount does not register as an operator change.
   */
  readonly layoutRevision: number;

  /**
   * Toggle a panel's open state per Stream C PR 2 + Flow P
   * 2026-05-26 amendment §"Add panel section":
   *
   * - If already open, **focus** it — call
   *   ``dockview.getPanel(id)?.api.setActive()`` to bring its
   *   tab to the front in whatever group hosts it.
   * - If closed, **add** it via ``dockview.addPanel({...})`` at
   *   the active group's tab list (dockview's default when no
   *   position is specified).
   *
   * Returns ``true`` when the panel was added; ``false`` when
   * already open (focus instead). Used by the menu's tests +
   * any future scripted callers.
   *
   * The Add panel menu is **role-filtered** by the consumer
   * (the menu calls :func:`getVisiblePanels` with the session's
   * ``hasAnyRole``); this method itself accepts any
   * :type:`PanelId` and trusts the caller for authorization.
   */
  togglePanel: (id: PanelId) => boolean;

  /**
   * **Activate** an already-open panel — bring its tab to the front in
   * whatever group hosts it (``dockview.getPanel(id)?.api.setActive()``).
   *
   * Unlike :func:`togglePanel`, this NEVER opens a closed panel: when ``id``
   * isn't currently in the layout (closed, or role-filtered out of this
   * session's workspace) it is a no-op. Returns ``true`` when the panel
   * existed and was activated, ``false`` otherwise.
   *
   * Drives the map→list **reveal** (REQ-UI-051 / ADR-024 §5): selecting a
   * glyph on the map surfaces the corresponding Platforms / Detections /
   * Alerts list — if present but sitting behind another tab — so the
   * synchronized selection is visible without a manual tab switch
   * (:func:`useRevealMapSelectionInList`).
   */
  activatePanel: (id: PanelId) => boolean;

  /**
   * Capture the current dockview state into a :type:`LayoutSettings`
   * (the two-tier opaque model — semantic ``panels[]`` + the stringified
   * ``dockview_state`` blob; ADR-024 §6) **without persisting** — no
   * ``setWorkingState`` / ``PUT``. Returns ``null`` when the dockview API
   * isn't ready yet (cold-mount window before ``onReady``).
   *
   * This is the read half of the Profile editor's Arrange-layout sub-mode
   * (REQ-UI-077): the editor snapshots on enter (to restore on Cancel) and
   * again on Done (to write into ``draft.layout`` + live-preview). Contrast
   * with :func:`saveCurrentLayout`, which captures **and** persists — this
   * primitive deliberately stops short of the PUT so an in-flight edit never
   * mutates the operator's saved working state.
   */
  captureLayout: () => LayoutSettings | null;

  /**
   * Load a :type:`LayoutSettings` into the live dockview via the same
   * ``fromJSON`` restore path the workspace uses on mount (role-gating +
   * corrupt-fallback to Single Map included) **without persisting** — no
   * ``setWorkingState`` / ``PUT``. No-op when the dockview API isn't ready.
   *
   * The write half of the Arrange-layout sub-mode (REQ-UI-077): the editor
   * calls this to **restore** the pre-arrange workspace on Cancel. The
   * ``dockview_state`` blob is treated opaquely — parsed only for the
   * ``fromJSON`` apply, never reshaped (ADR-024 §6).
   */
  applyLayout: (layout: LayoutSettings) => void;
}

/**
 * Value context — consumes the registered API. ``null`` when
 * no workspace is mounted in the current route subtree.
 *
 * **Exported for testing** — unit tests that mount components
 * consuming :func:`useMultiPaneApi` (e.g., the preferences-menu
 * Panels section) without the full workspace can wrap with
 * ``<MultiPaneApiValueContext.Provider value={mockApi}>``
 * directly. Production code should use
 * :class:`MultiPaneApiProvider` instead.
 */
export const MultiPaneApiValueContext = createContext<MultiPaneApi | null>(null);

/**
 * Setter context — the workspace calls this in a ``useEffect``
 * to publish its API on mount + ``null`` on unmount. Separated
 * from the value context so consumers of :func:`useMultiPaneApi`
 * don't re-render when the workspace re-registers a stable API.
 */
const MultiPaneApiSetterContext = createContext<((api: MultiPaneApi | null) => void) | null>(null);

interface MultiPaneApiProviderProps {
  children: ReactNode;
}

/**
 * Provider component — wraps the AppShell (or higher) so both
 * the topbar's :class:`PreferencesMenu` and the route-mounted
 * :class:`MultiPaneWorkspace` share the same context.
 */
export function MultiPaneApiProvider({ children }: MultiPaneApiProviderProps): React.ReactElement {
  const [api, setApi] = useState<MultiPaneApi | null>(null);
  // ``setApi`` from ``useState`` is stable across renders; wrap
  // in ``useCallback`` for the explicit-API discipline anyway so
  // a future refactor that swaps the storage substrate (Zustand,
  // useRef + custom store, etc.) doesn't change the consumer
  // contract.
  const stableSetApi = useCallback((next: MultiPaneApi | null) => setApi(next), []);
  return (
    <MultiPaneApiSetterContext.Provider value={stableSetApi}>
      <MultiPaneApiValueContext.Provider value={api}>{children}</MultiPaneApiValueContext.Provider>
    </MultiPaneApiSetterContext.Provider>
  );
}

/**
 * Hook for the preferences-menu (and other future consumers) to
 * read the workspace API. Returns ``null`` when no workspace is
 * mounted — callers gate their UI accordingly (the Layout
 * section of the popover hides; the ``Ctrl+Shift+0`` keyboard
 * shortcut is also gated on this).
 */
export function useMultiPaneApi(): MultiPaneApi | null {
  return useContext(MultiPaneApiValueContext);
}

/**
 * Hook for the workspace component to register its API. The
 * workspace calls the returned setter in a ``useEffect`` on
 * mount + ``null`` on unmount. Throws if no provider is mounted
 * — fail-fast discipline per CLAUDE.md §8.1.
 *
 * Wraps the returned identity in :func:`useMemo` so the
 * workspace's effect doesn't re-fire on every render.
 */
export function useRegisterMultiPaneApi(): (api: MultiPaneApi | null) => void {
  const setter = useContext(MultiPaneApiSetterContext);
  return useMemo(() => {
    if (setter === null) {
      throw new Error("useRegisterMultiPaneApi: MultiPaneApiProvider not mounted in this subtree.");
    }
    return setter;
  }, [setter]);
}
